import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import CreateConversationTab from './CreateConversationTab';
import AddUserConversationPopup from './AddUserConversationPopup';

import { useAuth } from '../../contexts/AuthContext';
import useMessagerie from '../../hooks/useMessagerie';
import useUsers from '../../hooks/useUsers';

import CustomSelect from '../../utils/CustomSelect';
import { formatDate } from '../../utils/Format'

import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { capitalizeEach, formatMessageDate } from '../../utils/Format';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.locale('fr');

const Messagerie = ({ Externe = null }) => {

    const { user } = useAuth();
    const { users, fetchUsers } = useUsers();
    const { conversations, messages, lastSeenMessage, fetchConversations, fetchMessagesByConversationUUID, fetchLastSeenMessage, createMessage, addParticipantsToConv, loading, error } = useMessagerie();

    const [selectedTab, setSelectedTab] = useState('listconv');

    const [selectedConversation, setSelectedConversation] = useState(null);

    const [hasInteracted, sethasInteracted] = useState(false);

    const [popupVisible, setPopupVisible] = useState(null);


    const unreadMarkerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);

    const [searchQuery, setSearchQuery] = useState('');

    const [inputMessage, setInputMessage] = useState('');
    const [pendingMessages, setPendingMessages] = useState([]);

    useEffect(() => {
        fetchConversations();

        const interval = setInterval(() => {
            fetchConversations();
        }, 10000);

        return () => clearInterval(interval);
    }, [selectedConversation?.UUID_]);

    useEffect(() => {
        if (!selectedConversation) return;

        fetchMessagesByConversationUUID(selectedConversation.UUID_);
        setPendingMessages([])

        const interval = setInterval(() => {
            fetchMessagesByConversationUUID(selectedConversation.UUID_);
            setPendingMessages([])
        }, 10000);

        return () => clearInterval(interval);
    }, [selectedConversation?.UUID_]);

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || !selectedConversation) return;

        // console.log("Envoi du message:", inputMessage, "à la conversation:", selectedConversation);
        sethasInteracted(true);

        const tempId = Date.now();
        const tempMessage = {
            UUID_: tempId,
            UUID_Utilisateur: user.UUID_,
            Nom_Utilisateur: capitalizeEach(user.Nom),
            Type: 'texte',
            Contenu_Texte: inputMessage,
            Date_Crea: dayjs().format('YYYY-MM-DD'),
            Heure_Crea: dayjs().hour() * 3600 + dayjs().minute() * 60 + dayjs().second(),
            Status: 'sending',
        };

        //console.log("Message temporaire créé:", tempMessage);

        setInputMessage('');

        setPendingMessages((prev) => [...prev, tempMessage]);

        const result = await createMessage("texte", inputMessage, selectedConversation.UUID_);

        if (result && result.status == "success") {

            setPendingMessages((prev) =>
                prev.map((m) => (m.UUID_ === tempId ? { ...m, Status: 'sent' } : m))
            );
        } else {
            setPendingMessages((prev) =>
                prev.map((m) => (m.UUID_ === tempId ? { ...m, Status: 'error' } : m))
            );
        }

        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        // console.log(pendingMessages);
    };

    const filteredConversations = conversations.filter((conv) => {
        const nomConv = conv.Nom?.toLowerCase() || '';
        const query = searchQuery.trim().toLowerCase();

        return nomConv.includes(query);
    });

    const togglePopup = (type) => {
        setPopupVisible(prev => prev === type ? null : type);
    };

    // console.log("User:", user);
    // console.log("Conversations:", conversations);
    // console.log("Messages:", messages);
    // console.log("Selected Conversation:", selectedConversation);

    return (
        <>
            <div className="flex justify-between items-center pb-10">
                <p
                    tabIndex="0"
                    className={`focus:outline-none font-semibold ${Externe ? "" : "text-gray-200"} text-2xl leading-6`}
                >
                    Messagerie
                </p>
            </div>

            <div className="relative flex-1 w-full overflow-hidden">
                <div
                    className="flex w-[100%] h-full transition-transform duration-500 ease-in-out"
                    style={{
                        transform:
                            selectedTab === 'createconv'
                                ? 'translateX(0%)'
                                : selectedTab === 'listconv'
                                    ? 'translateX(-100%)'
                                    : 'translateX(-200%)',
                    }}
                >
                    {/*1 Création conv*/}

                    <CreateConversationTab
                        loggedUser={user}
                        users={users}
                        setSelectedTab={setSelectedTab}
                        fetchConversations={fetchConversations}
                    />

                    {/*2 Liste de conv*/}
                    <div className="flex-shrink-0 w-full h-full">
                        <div className="flex justify-between items-center bg-gray-800 mb-4 p-3 rounded-xl">
                            <div className="flex items-center gap-3">
                                <img
                                    src="https://cdn-icons-png.flaticon.com/512/149/149071.png"
                                    alt=""
                                    className="border border-gray-600 rounded-full w-12 h-12 object-cover"
                                />
                                <div>
                                    <p className="font-medium text-white">{capitalizeEach(user.Nom)}</p>
                                    <p className="text-green-400 text-xs">En ligne</p>
                                </div>
                            </div>

                            <button className="bg-orange-600 hover:bg-orange-500 px-3 py-1 rounded-md font-semibold text-white text-xs transition"
                                onClick={() => {
                                    setSelectedTab('createconv');
                                    fetchUsers();
                                    setSelectedConversation(null);
                                    sethasInteracted(false);
                                }}>
                                Nouvelle Conversation
                            </button>
                        </div>

                        <div className="flex justify-center items-center mb-4">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Rechercher une conversation..."
                                className="bg-gray-700 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-600 w-[99%] text-white text-sm transition placeholder-gray-400"
                            />
                        </div>
                        {filteredConversations.length > 0 ? (
                            filteredConversations.map((conversation, index) => (
                                <div
                                    onClick={async () => {
                                        setSelectedTab('conv')
                                        const lastSeen = await fetchLastSeenMessage(conversation?.UUID_); setSelectedConversation(conversation);
                                        setTimeout(() => {
                                            if (lastSeen && unreadMarkerRef.current && messagesContainerRef.current) {
                                                const container = messagesContainerRef.current;
                                                const marker = unreadMarkerRef.current;

                                                const offsetTop = marker.offsetTop - container.offsetTop;
                                                container.scrollTop = offsetTop;
                                            } else if (messagesContainerRef.current) {
                                                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                                            }
                                        }, 50);
                                    }}
                                    key={index}
                                    uuid={conversation.UUID_}
                                    className={`flex items-center gap-3 mb-3 ${conversation.NonLus > 0 ? "bg-gray-700 hover:bg-gray-600/80" : "bg-gray-800 hover:bg-gray-700"} p-4 rounded-xl transition-all duration-200 cursor-pointer`}
                                >
                                    <img
                                        src={"https://png.pngtree.com/png-vector/20191009/ourmid/pngtree-group-icon-png-image_1796653.jpg"}
                                        alt=""
                                        className="border border-gray-600 rounded-full w-12 h-12 object-cover"
                                    />
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center">
                                            <p className="font-medium text-white truncate">{conversation?.Nom}</p>
                                            <span className={`${conversation.NonLus == 0 && "hidden"} bg-orange-600 px-1.5 rounded-full text-white text-xs`}>
                                                {conversation.NonLus}
                                            </span>
                                            <div className="flex justify-center -space-x-[15px]">
                                                {conversation.Participants.map((participant, index) => (
                                                    <div key={index} className="bg-gray-200 p-[2px] rounded-full">
                                                        <img
                                                            src={participant.src ? participant.src : "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                                            alt={`Avatar ${index}`}
                                                            className="rounded-full w-[25px] h-[25px] object-cover"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <p className="text-gray-400 text-xs truncate">
                                                {conversation.DernierMessage ? (
                                                    conversation.DernierMessage?.Type === "texte"
                                                        ? `${capitalizeEach(conversation.DernierMessage?.UserNom)} : ${conversation.DernierMessage?.Contenu_Texte.length > 50
                                                            ? conversation.DernierMessage.Contenu_Texte.slice(0, 50) + '...'
                                                            : conversation.DernierMessage?.Contenu_Texte}`
                                                        : `${capitalizeEach(conversation.DernierMessage?.UserNom)} a envoyé un ${conversation.DernierMessage?.Type}`
                                                ) : (
                                                    conversation.UUID_createur === user.UUID_
                                                        ? "Vous avez créé cette conversation"
                                                        : `Conversation créée par ${capitalizeEach(conversation?.CreateurNom)}`
                                                )}
                                            </p>

                                            {conversation.DernierMessage ? (
                                                <span className="text-gray-400 text-xs">
                                                    {formatMessageDate(conversation.DernierMessage?.Date_Crea, conversation.DernierMessage?.Heure_Crea)}
                                                </span>
                                            ) :
                                                <span className="text-gray-400 text-xs">
                                                    {formatMessageDate(conversation.Date_Crea, conversation.Heure_Crea)}
                                                </span>
                                            }
                                        </div>

                                    </div>
                                </div>))
                        ) : (
                            <div className="bg-gray-800 mt-5 p-6 border border-gray-700 rounded-xl text-gray-400 text-center">
                                <p className="mb-2 font-semibold text-white text-lg">Aucune conversation trouvée</p>
                                <p className="text-gray-400 text-sm">Souhaitez-vous en créer une ?</p>
                                <button
                                    className="bg-orange-600 hover:bg-orange-500 mt-4 px-4 py-2 rounded-md text-white text-sm transition"
                                    onClick={() => {
                                        setSelectedTab('createconv');
                                        fetchUsers();
                                        setSelectedConversation(null);
                                        sethasInteracted(false);
                                    }}
                                >
                                    Créer une conversation
                                </button>
                            </div>
                        )}
                    </div>

                    {/*3 Conv*/}
                    <div className="flex flex-col flex-shrink-0 justify-between gap-3 w-full">
                        <div className="flex justify-between items-center gap-4 bg-gray-800 mb-4 px-3 py-4 rounded-xl">
                            <div
                                onClick={() => {
                                    setSelectedTab('listconv');
                                    setSelectedConversation(null);
                                    sethasInteracted(false);
                                }}
                                class="group flex justify-center items-center w-10 h-10 cursor-pointer">
                                <i class="fa-chevron-left text-gray-200 group-hover:text-orange-200 transition-all duration-300 fa-solid fa-lg"></i>
                            </div>
                            <div className="flex flex-1 items-center gap-3">
                                <img
                                    src={"https://png.pngtree.com/png-vector/20191009/ourmid/pngtree-group-icon-png-image_1796653.jpg"}
                                    alt=""
                                    className="border border-gray-600 rounded-full w-10 h-10 object-cover"
                                />
                                <div>
                                    <p className="font-medium text-white">{selectedConversation?.Nom}</p>
                                    <p className="text-gray-300 text-xs">
                                        {(() => {
                                            if (!selectedConversation?.Participants || !user) return 'Aucun participant';

                                            const displayLimit = 6;

                                            const formattedNames = selectedConversation?.Participants.map((p) =>
                                                p.UUID_ === user.UUID_ ? 'Vous' : capitalizeEach(p.Nom)
                                            );

                                            const visible = formattedNames.slice(0, displayLimit);
                                            const hiddenCount = formattedNames.length > displayLimit
                                                ? formattedNames.length - displayLimit
                                                : 0;

                                            return hiddenCount > 0
                                                ? `${visible.join(', ')}... +${hiddenCount}`
                                                : visible.join(', ');
                                        })()}
                                    </p>

                                </div>
                            </div>

                            <div className="flex *:flex *:justify-center *:items-center *:w-9 h-9 cursor-pointer">
                                <i className="text-gray-200 hover:text-orange-200 transition-all duration-300 cursor-pointer fa-solid fa-magnifying-glass" />


                                <div className="relative">
                                    <i className="text-gray-200 hover:text-orange-200 transition-all duration-300 cursor-pointer fa-solid fa-thumbtack"
                                        onClick={() => togglePopup("pin")} />
                                    {popupVisible === "pin" && (
                                        <div className="top-10 right-0 z-50 absolute bg-gray-700 shadow-md px-4 py-2 border border-gray-600 rounded-md text-white text-sm">
                                            Épingler
                                        </div>
                                    )}
                                </div>

                                <div className="relative">
                                    <i className="text-gray-200 hover:text-orange-200 transition-all duration-300 cursor-pointer fa-solid fa-user-plus"
                                        onClick={() => {
                                            fetchUsers();
                                            togglePopup("add")
                                        }} />
                                    {popupVisible === "add" && (
                                        <AddUserConversationPopup users={users} InConvUsers={selectedConversation?.Participants} onClose={() => setPopupVisible(false)}
                                            onAdd={async (selectedUUIDs) => {
                                                try {
                                                    const response = await addParticipantsToConv(selectedConversation.UUID_, selectedUUIDs);
                                                    if (response?.message === "success") {
                                                        fetchConversations();
                                                    }
                                                } catch (error) {
                                                    console.error("Erreur lors de l'ajout des participants :", error);
                                                    alert("Une erreur est survenue.");
                                                }
                                            }}
                                        />
                                    )}
                                </div>

                                <div className="relative">
                                    <i className="text-gray-200 hover:text-orange-200 hover:rotate-[20deg] transition-all duration-300 cursor-pointer fa-solid fa-gear"
                                        onClick={() => togglePopup("settings")} />
                                    {popupVisible === "settings" && (
                                        <div className="top-10 right-0 z-50 absolute bg-gray-700 shadow-md px-4 py-2 border border-gray-600 rounded-md text-white text-sm">
                                            Paramètres
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>

                        <div ref={messagesContainerRef} class="flex flex-col flex-grow p-4 h-0 overflow-y-scroll scroll-orange">
                            {[...messages, ...pendingMessages]
                                .sort((a, b) => {
                                    const dateA = dayjs(a.Date_Crea).add(a.Heure_Crea, 'second');
                                    const dateB = dayjs(b.Date_Crea).add(b.Heure_Crea, 'second');
                                    return dateA.isAfter(dateB) ? 1 : -1;
                                })
                                .map((message, index) => {
                                    const messageCreator = message.UUID_Utilisateur === user.UUID_ ? "Vous" : capitalizeEach(message.Nom_Utilisateur);
                                    // console.log("user:", user, "message:", message, "creator:", messageCreator, "||||||||||||", "message.UUID_Utilisateur === user.UUID_", message.UUID_Utilisateur, user.UUID_);
                                    const isCurrentUser = message.UUID_Utilisateur === user.UUID_;
                                    const isCreator = selectedConversation?.UUID_Createur === message.UUID_Utilisateur;
                                    const isAllUnread = lastSeenMessage?.message === "aucun message lu";
                                    const isSysteme = message.Type === "systeme";
                                    const isUnreadMarker = message.UUID_ === lastSeenMessage?.UUID_ && lastSeenMessage?.UUID_ !== null 
                                    && lastSeenMessage?.UUID_ !== messages[0]?.UUID_ && lastSeenMessage?.message !== "success";                                 const selectedConversationCreator = selectedConversation?.UUID_Createur === user.UUID_ ? "Vous" : selectedConversation?.CreateurNom;
                                    const messageClass = isCurrentUser
                                        ? 'bg-orange-600 text-white rounded-l-lg rounded-br-lg ml-auto'
                                        : 'bg-gray-300 text-gray-800 rounded-r-lg rounded-bl-lg';

                                    // console.log(isUnreadMarker, hasInteracted, lastSeenMessage);
                                    return (
                                        <>
                                            {!isSysteme ? (
                                                <>
                                                    {isAllUnread && hasInteracted && (
                                                        <div ref={unreadMarkerRef} className="flex items-center my-2 w-full">
                                                            <div className="flex-grow bg-orange-500 h-px"></div>
                                                            <span className="px-2 text-orange-500 text-xs">Nouveaux messages</span>
                                                            <div className="flex-grow bg-orange-500 h-px"></div>
                                                        </div>
                                                    )}

                                                    <div key={index} className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} space-x-3 mt-1 w-full`}>
                                                        {!isCurrentUser && (
                                                            <div className="flex flex-col">
                                                                <div className="flex justify-center items-center border rounded-full w-10 h-10">
                                                                    <img
                                                                        src={message.image === '' || message.image == null ? 'https://cdn-icons-png.flaticon.com/512/149/149071.png' : ''}
                                                                        alt=""
                                                                    />
                                                                </div>
                                                                {isCreator && (
                                                                    <div className="mt-2">
                                                                        <div className="bg-yellow-500 rounded-full w-3 h-3"></div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        <div className="max-w-[50%]">
                                                            <span className={`text-gray-400 text-sm w-full ${isCurrentUser ? 'text-right' : 'justify-start'}`}>
                                                                {capitalizeEach(message.Nom_Utilisateur)}
                                                            </span>
                                                            <div className={`${messageClass} p-3`}>
                                                                <p className="text-sm break-words">{message.Contenu_Texte}</p>
                                                            </div>
                                                            <span className="text-gray-500 text-xs leading-none">
                                                                {message.Status === 'sending'
                                                                    ? 'Envoi...'
                                                                    : message.Status === 'error'
                                                                        ? 'Erreur'
                                                                        : formatMessageDate(message.Date_Crea, message.Heure_Crea)}
                                                            </span>
                                                        </div>

                                                        {isCurrentUser && (
                                                            <div className="flex flex-col">
                                                                <div className="flex justify-center items-center border rounded-full w-10 h-10">
                                                                    <img
                                                                        src={message.image === '' || message.image == null ? 'https://cdn-icons-png.flaticon.com/512/149/149071.png' : ''}
                                                                        alt=""
                                                                    />
                                                                </div>
                                                                {isCreator && (
                                                                    <div className="mt-2">
                                                                        <div className="bg-yellow-500 rounded-full w-3 h-3"></div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {isUnreadMarker && !hasInteracted && (
                                                        <div ref={unreadMarkerRef} className="flex items-center my-2 w-full">
                                                            <div className="flex-grow bg-orange-500 h-px"></div>
                                                            <span className="px-2 text-orange-500 text-xs">Nouveaux messages</span>
                                                            <div className="flex-grow bg-orange-500 h-px"></div>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="flex items-center my-2 w-full">
                                                    <div className="flex-grow bg-gray-500 h-px"></div>
                                                    {message.Contexte.Type === "createConv" ? (
                                                        <span className="px-2 max-w-[80%] text-orange-400 text-xs text-center">{selectedConversationCreator === "Vous" ? "Vous avez "
                                                            : selectedConversationCreator + " a "}
                                                            créé la Conversation {formatMessageDate(message.Date_Crea, message.Heure_Crea)}</span>
                                                    )  : message.Contexte.Type === "addParticipants" ? (
                                                        <span className="px-2 max-w-[80%] text-gray-400 text-xs"><span class="text-orange-400">{messageCreator === "Vous" ? "Vous "
                                                            : messageCreator}</span>
                                                            {messageCreator === "Vous" ? "avez " : " a " }
                                                            ajouté {message.Contexte.AddedParticipants.map(
                                                                (p, idx) => (
                                                                    <span key={p.UUID_} className="font-semibold text-orange-400">
                                                                        {capitalizeEach(p.Nom)}{idx < message.Contexte.AddedParticipants.length - 1 ? ', ' : ''}
                                                                    </span>
                                                                )
                                                            )} aux participants de la conversation <span class="font-semibold">{formatMessageDate(message.Date_Crea, message.Heure_Crea)}</span></span>
                                                    ) : null}
                                                    <div className="flex-grow bg-gray-500 h-px"></div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })}
                        </div>

                        <div class="flex flex-row items-center bg-white px-2 rounded-xl w-full h-16">
                            <div>
                                <button
                                    class="flex justify-center items-center p-2 text-gray-400 hover:text-gray-600 transition duration-300 ease-in-out"
                                >
                                    <svg
                                        class="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                        ></path>
                                    </svg>
                                </button>
                            </div>
                            <div class="flex-grow mx-2">
                                <div class="relative w-full">
                                    <input
                                        type="text"
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                        placeholder="Tapez votre message..."
                                        class="flex pl-4 border-2 focus:border-orange-300 rounded-lg focus:outline-none w-full h-10 transition duration-300 ease-in-out"
                                    />
                                    <button
                                        class="top-0 right-0 absolute flex justify-center items-center w-12 h-full text-gray-400 hover:text-gray-600 transition duration-300 ease-in-out"
                                    >
                                        <svg
                                            class="w-6 h-6"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            ></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div class="p-2 text-gray-400 hover:text-gray-600 transition duration-300 ease-in-out cursor-pointer">
                                <i class="fa-solid fa-microphone fa-lg"></i>
                            </div>
                            <div class="ml-2">
                                <button
                                    onClick={handleSendMessage}
                                    class="flex flex-shrink-0 justify-center items-center bg-orange-600 hover:bg-orange-500 px-4 py-2 rounded-lg text-white transition duration-300 ease-in-out"
                                >
                                    <span>Envoyer</span>
                                    <span class="ml-2">
                                        <svg
                                            class="-mt-px w-4 h-4 rotate-45 transform"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                                            ></path>
                                        </svg>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Messagerie;
